import {getCookie, removeCookie, setCookie} from "react-use-cookie";
import {createAsyncThunk, createSelector, createSlice} from "@reduxjs/toolkit";
import Swal from "sweetalert2";

import {fetchData, fetchDataNew, fetchList} from "../hook/axios.hook";
import {findIfUserIsITManager, SUPER_USER_POSITION_ID} from "../utilities/generalConstants.js/generalConstants";
import {toaster} from "../utilities/hooks/toaster";

const token = getCookie("access_token");

export const getError = error => {
  toaster.error(error?.message || String(error));
};
export const getInfo = text => {
  toaster.info(text);
};

const initialState = {
  user: null,
  isAuth: !!token,
  authLoadingStatus: "idle",
  // token: token ? token : null,
  facsimile: null,
  systemLanguage: "en",
  // accessRoutes: null,
  previousUserLocation: null,
  hiddenContainerState: {},
};

export const login = createAsyncThunk("user/login", async body => {
  return fetchData("/auth/login", "post", body);
});

export const logout = createAsyncThunk("user/logout", async () => {
  return fetchData("/auth/logout", "post");
});

export const changePassword = createAsyncThunk("user/changePassword", async newPasw => {
  return fetchData("/users/changePass", "put", newPasw);
});

export const getUser = createAsyncThunk("user/getUser", async () => {
  return fetchData("/users/getCurrentUser", "get");
});
export const updateUser = createAsyncThunk("user/updateUser", async ({userId, body}, {signal, rejectWithValue}) => {
  return fetchDataNew(`/users/${userId}`, "patch", {body, signal, rejectWithValue});
});

export const upsertSettings = createAsyncThunk("user/upsertSettings", async ({type, data}, {rejectWithValue}) => {
  return fetchData(`/users/settings/${type}`, "post", {data}, null, false, null, false).then(response => {
    if (response) return response;
    return rejectWithValue("Failed to upsert user settings");
  });
});

export const createFacsimile = createAsyncThunk("user/createFacsimile", async formData => {
  return fetchData("/facsimile/create", "post", formData);
});

export const getFacsimileForCurrentUser = createAsyncThunk("user/getFacsimileForCurrentUser", async () => {
  return fetchData("/facsimile/getForCurrent", "get");
});

export const fetchAccessRoutes = createAsyncThunk("person/fetchAccessRoutes", async () => {
  return fetchList("/routes/getAll");
});

export const registrUser = async user => {
  fetchData("/users/add", "post", user).then(res => {
    if (Object.prototype.hasOwnProperty.call(res, "persona")) {
      // getInfo(`Користувача ${res.persona.CA_FULL_NAME} успішно зареєстровано`);
      Swal.fire({
        title: `Користувача ${res.persona.DA_EMPLOYEE_NAME} успішно зареєстровано`,
        text: `Логін: ${res.persona.DA_LOGIN}`,
        icon: "info",
        confirmButtonText: "Ок",
      });
    }
  });
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setNotAuth: state => {
      state.isAuth = false;
      state.user = null;
    },
    addChosenUser: (state, payload) => {
      state.chosenUser = payload;
    },
    addFacsimile: (state, {payload}) => {
      state.facsimile = payload;
    },
    setSystemLanguage: (state, {payload}) => {
      state.systemLanguage = payload;
    },
    setPreviousUserLocation: (state, {payload}) => {
      state.previousUserLocation = payload;
    },
    clearPreviousUserLocation: state => {
      state.previousUserLocation = null;
    },
    setHiddenContainerState: (state, {payload}) => {
      state.hiddenContainerState = {...state.hiddenContainerState, [payload?.window]: payload?.value};
    },
  },
  extraReducers: builder => {
    builder
      .addCase(login.pending, state => {
        state.authLoadingStatus = "loading";
      })
      .addCase(login.fulfilled, (state, {payload}) => {
        state.authLoadingStatus = "idle";
        if (!payload || typeof payload !== "object") {
          return;
        }
        if (Object.prototype.hasOwnProperty.call(payload, "message")) {
          getInfo(payload.message);
          state.user = payload.userId;
          state.isAuth = false;
          // localStorage.removeItem("access_token");
          removeCookie("access_token");
          localStorage.removeItem("login_time");
        }
        if (Object.prototype.hasOwnProperty.call(payload, "token")) {
          // state.token = payload.token;
          state.isAuth = true;
          state.user = payload.user;
          // localStorage.setItem("access_token", payload.token);
          setCookie("access_token", payload.token, {
            days: 1,
            Secure: true,
            SameSite: "Strict",
          });
          localStorage.setItem("login_time", Date.now());
        }
      })
      .addCase(login.rejected, state => {
        state.authLoadingStatus = "idle";
        state.isAuth = false;
        state.user = null;
      })
      .addCase(changePassword.fulfilled, (state, {payload}) => {
        if (!payload) return;

        state.isAuth = true;
        // state.token = payload.token;
        state.user = payload.user;
        // localStorage.setItem("access_token", payload.token);
        setCookie("access_token", payload.token, {
          days: 1,
          Secure: true,
          SameSite: "Strict",
        });

        localStorage.setItem("login_time", Date.now());
        getInfo("Пароль успішно змінено!");
      })
      .addCase(logout.fulfilled, state => {
        // localStorage.removeItem("access_token");
        removeCookie("access_token");
        localStorage.removeItem("login_time");
        state.isAuth = false;
        state.user = null;
      })
      .addCase(getUser.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.user = {
          login: payload.login,
          id: payload.userId,
          firstName: payload.persona.firstName,
          lastName: payload.persona.lastName,
          engName: payload.persona.engFirstName,
          engLastName: payload.persona.engLastName,
          orgId: payload.orgStructureId,
          orgStructure: payload.orgStructure,
          name: payload.name,
          position: payload.position,
        };
        state.facsimile = payload.facsimile;
      })
      .addCase(updateUser.fulfilled, (state, {payload}) => {
        state.user = {...state.user, ...payload};
      })
      .addCase(createFacsimile.fulfilled, (state, {payload}) => {
        state.facsimile = payload;
      })
      .addCase(getFacsimileForCurrentUser.fulfilled, (state, {payload}) => {
        state.facsimile = payload;
      })
      .addCase(upsertSettings.fulfilled, (state, {payload, meta}) => {
        if (!state.user) return;

        const type = meta?.arg?.type;

        if (payload?.settings) {
          state.user.settings = payload.settings;
          return;
        }

        if (!type) return;
        state.user.settings = {
          ...(state.user.settings || {}),
          [type]: meta?.arg?.data,
        };
      })
      .addCase(getUser.rejected, state => {
        state.isAuth = false;
        state.user = null;
      })

      // .addCase(fetchAccessRoutes.fulfilled, (state, {payload}) => {
      //   state.accessRoutes = payload;
      // })

      .addDefaultCase(() => {});
  },
});

const {actions, reducer} = userSlice;

export const {
  setNotAuth,
  addFacsimile,
  setSystemLanguage,
  setPreviousUserLocation,
  clearPreviousUserLocation,
  setHiddenContainerState,
} = actions;

export default reducer;

export const getCurrentUser = state => state.user.user;
export const selectCurrentUserId = state => state.user?.user?.id;
export const selectCurrentUser = state => state.user?.user;
export const selectUserOrgstructureId = state => state.user?.user?.orgStructure?.orgStructureId;
export const getCurrentUserRole = state => state.user?.user?.position?.positionId || null;
export const getCurrentUserRoutes = state => state.user.user?.position?.routes || null;
export const selectCurrentUserPosition = state => state.user?.user?.position || null;
// export const getAccessRoutes = state => state.user.accessRoutes;
// export const selectAllRoutes = state => state.user.accessRoutes;

// export const getToken = state => state.user.token;
export const selectIsAuth = state => state.user.isAuth;
export const selectFacsimile = state => state.user.facsimile;

export const selectPreviousUserLocation = state => state.user.previousUserLocation;

export const getHiddenContainerState = state => state.user.hiddenContainerState;

export const selectAllPositionPermissions = createSelector([selectCurrentUser], user => {
  const routes = user?.position?.routes || [];

  return routes
    .flatMap(({settings}) => settings?.permissions || [])
    .reduce((acc, perm) => {
      acc[perm[0]] = perm[1];
      return acc;
    }, {});
});

export const selectIsSuperUser = createSelector([selectCurrentUser], user => {
  return user?.position?.positionId === SUPER_USER_POSITION_ID;
});
export const selectIsItManager = createSelector([selectCurrentUser], user => {
  return findIfUserIsITManager(user);
});
